import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  StickyNote, Mail, Phone, FileText, Calculator, ReceiptText, Banknote, MonitorSmartphone,
  FileMinus2, Wallet, FolderKanban, ListTodo, FileSignature, LifeBuoy, Boxes,
  ExternalLink, type LucideIcon,
} from "lucide-react";
import { Badge, Loader, cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { activityTypeLabel } from "../../../lib/activityType";
import { type TimelineEvent, type TimelineKind, getClientTimeline } from "../../../lib/clients";
import { formatCents } from "../../../lib/sales";

type T = ReturnType<typeof useT>;

/**
 * The client's activity log: every event tied to one client on one timeline.
 *
 * Its own file rather than another section of `ClientsSection.tsx`. That file
 * holds the client card, its six tabs and the modals around them, and this is
 * the one tab that answers a different question - not "what does this record
 * say" but "what has happened to this customer". It also owns the two maps that
 * every new timeline source has to be added to, and those are much easier to
 * find under a name that says what they are for.
 *
 * Moved verbatim (AGENTS.md §4.6): no behaviour changed in the move, only the
 * component's name, which now matches the file's.
 */
// ── Activity log (one timeline merging every event tied to this client) ─────────
const KIND_META: Record<TimelineKind, { icon: LucideIcon; dot: string }> = {
  note:        { icon: StickyNote,    dot: "bg-slate-400/15 text-slate-500" },
  email:       { icon: Mail,          dot: "bg-sky-500/15 text-sky-500" },
  call:        { icon: Phone,         dot: "bg-lime-500/15 text-lime-600" },
  proposal:    { icon: FileText,      dot: "bg-violet-500/15 text-violet-500" },
  estimate:    { icon: Calculator,    dot: "bg-indigo-500/15 text-indigo-500" },
  invoice:     { icon: ReceiptText,   dot: "bg-emerald-500/15 text-emerald-600" },
  payment:     { icon: Banknote,      dot: "bg-green-500/15 text-green-600" },
  credit_note: { icon: FileMinus2,    dot: "bg-rose-500/15 text-rose-500" },
  expense:     { icon: Wallet,        dot: "bg-amber-500/15 text-amber-600" },
  project:     { icon: FolderKanban,  dot: "bg-blue-500/15 text-blue-500" },
  task:        { icon: ListTodo,      dot: "bg-cyan-500/15 text-cyan-600" },
  contract:    { icon: FileSignature, dot: "bg-teal-500/15 text-teal-600" },
  ticket:      { icon: LifeBuoy,      dot: "bg-orange-500/15 text-orange-500" },
  package:     { icon: Boxes,         dot: "bg-fuchsia-500/15 text-fuchsia-500" },
  portal:      { icon: MonitorSmartphone, dot: "bg-emerald-500/15 text-emerald-600" },
};
/**
 * The order the chips appear in. `portal` sits at the front, ahead of even the
 * mail: it is the only lane on this timeline that the CUSTOMER wrote, it is the
 * rarest, and a chip that is usually absent is worth seeing the moment it is
 * not.
 */
const KIND_ORDER: TimelineKind[] = [
  "portal", "email", "call", "invoice", "payment", "estimate", "proposal", "credit_note",
  "project", "task", "contract", "ticket", "expense", "package", "note",
];

/**
 * The kinds whose title is a stored `Activity.type` rather than a sentence.
 *
 * The column is a key and the timeline is read in whichever language the
 * operator picked, so these get translated here and everything else is printed
 * as it arrived. Adding an activity-backed kind without adding it here puts a
 * raw database word on screen.
 *
 * `portal` is activity-backed and deliberately NOT in this list. Its type is the
 * single word "portal", which the lane label above the row already says; the API
 * sends its body as the title instead, so the row says what the customer did
 * rather than saying "Portal" twice.
 */
const ACTIVITY_KINDS: TimelineKind[] = ["note"];

export function ClientTimeline({ t, clientId }: { t: T; clientId: string }) {
  const [rows, setRows] = useState<TimelineEvent[]>([]);
  const [counts, setCounts] = useState<Partial<Record<TimelineKind, number>>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TimelineKind | "all">("all");

  useEffect(() => {
    setLoading(true);
    getClientTimeline(clientId, { limit: 400 })
      .then((r) => { setRows(r.rows); setCounts(r.counts); })
      .catch(() => { setRows([]); setCounts({}); })
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <Loader />;
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <StickyNote className="mx-auto mb-2 size-6 opacity-40" />
        {t("clients.timelineEmpty")}
      </div>
    );
  }

  const visible = filter === "all" ? rows : rows.filter((e) => e.kind === filter);
  const total = rows.length;

  return (
    <div className="space-y-4">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={t("clients.tl.all")} count={total} />
        {KIND_ORDER.filter((k) => counts[k]).map((k) => (
          <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} label={t(`clients.tl.${k}`)} count={counts[k] ?? 0} icon={KIND_META[k].icon} />
        ))}
      </div>

      {/* The timeline */}
      <ol className="relative space-y-1 before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-px before:bg-border">
        {visible.map((e) => {
          const meta = KIND_META[e.kind];
          const Icon = meta.icon;
          const inner = (
            <>
              <span className={cn("relative z-10 mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ring-4 ring-background", meta.dot)}>
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t(`clients.tl.${e.kind}`)}</span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                    {new Date(e.at).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* An activity has no title of its own, so the API sends its
                      type as one. Translated here rather than there - see
                      ACTIVITY_KINDS. */}
                  <p className="truncate font-medium">
                    {ACTIVITY_KINDS.includes(e.kind) ? activityTypeLabel(e.title, t) : e.title}
                  </p>
                  {e.status && <StatusBadge t={t} kind={e.kind} status={e.status} />}
                  {e.amountCents != null && (
                    <span className="ml-auto shrink-0 text-sm font-medium tabular-nums">{formatCents(e.amountCents, e.currency ?? "EUR")}</span>
                  )}
                  {e.link && <ExternalLink className="size-3 shrink-0 text-muted-foreground" />}
                </div>
                {e.subtitle && <p className="truncate text-xs text-muted-foreground">{e.subtitle}</p>}
                {e.actor && <p className="text-[11px] text-muted-foreground">{e.actor}</p>}
              </div>
            </>
          );
          return (
            <li key={e.id}>
              {e.link ? (
                <Link to={e.link} className="flex gap-3 rounded-lg p-2 transition-colors hover:bg-accent/50">{inner}</Link>
              ) : (
                <div className="flex gap-3 rounded-lg p-2">{inner}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, icon: Icon }: {
  active: boolean; onClick: () => void; label: string; count: number; icon?: LucideIcon;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        active ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-accent",
      )}
    >
      {Icon && <Icon className="size-3" />}
      {label}
      <span className={cn("rounded-full px-1 text-[10px] tabular-nums", active ? "bg-primary-foreground/20" : "bg-muted")}>{count}</span>
    </button>
  );
}

function StatusBadge({ t, kind, status }: { t: T; kind: TimelineKind; status: string }) {
  const tone: Record<string, string> = {
    paid: "success", accepted: "success", active: "success", resolved: "success", issued: "success", done: "success", signed: "success",
    overdue: "destructive", declined: "destructive", cancelled: "destructive", terminated: "destructive", expired: "destructive",
    unread: "default", sent: "secondary",
  };
  const variant = (tone[status] ?? "secondary") as "default" | "secondary" | "success" | "destructive" | "outline";
  const key = `clients.tlStatus.${status}`;
  const raw = t(key);
  const label = raw === key ? status.replace(/_/g, " ") : raw;
  return <Badge variant={variant} className="shrink-0 text-[10px]">{label}</Badge>;
}
